/* eslint-disable react/no-danger */
/* eslint-disable max-lines */
import { ErrorLogComponent } from '@cian/error-log-component';
import { getGlobalEventsLogs } from '@cian/events-log';
import { Spinner } from '@cian/ui-kit';
import classNames from 'classnames';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as style from './SearchEngineResultsPageContainer.css';
import { canShowTrap, getSuburbanCatalogsTypes, isBlackFridayBannerVisible } from './helpers';
import { getSeoTabsLinksAvailable } from './helpers/getSeoTabsLinksAvailable';
import { EarlyAccessPaywallContainer } from '../../../packages/EarlyAccessPaywall';
import { readAllOffersForSavedSearches } from '../../api/saved_searches/readAllOffers';
import { ChatsContainerLoadable } from '../../common/components/ChatsContainer';
import { PreInfiniteBanner } from '../../common/components/PreInfiniteBanner';
import { EPreInfiniteBannerTypes } from '../../common/components/PreInfiniteBanner/PreInfiniteBanner';
import { IAppState } from '../../common/state';
import { SeoTopBannersLayout } from '../../components/SeoTopBannersLayout';
import { residentialComplexLayoutsOnSelector } from '../../selectors/newbuildingLayouts';
import { selectIsNewbuildingPromoBuilderOffersVisible } from '../../selectors/newbuildingPromoBuilderOffers';
import { selectIsSpecialProjectAdfoxBannerVisible } from '../../selectors/newbuildingSpecialProject';
import { selectIsProfessionalSearchPromoBannerVisible } from '../../selectors/selectIsProfessionalSearchPromoBannerVisible';
import { selectIsSubsidisedMortgageZeroPlaceBannerVisible } from '../../selectors/selectIsSubsidisedMortgageZeroPlaceBannerVisible';
import { RealtorRequestTrapContainer } from '../../serp/components/demand';
import { FastLinksContainer } from '../../serp/components/fast_links/container';
import { HideOfferTopPopup } from '../../serp/components/hide_offer_top_popup';
import { NewobjectsContainer } from '../../serp/components/newobjects/container';
import { NewobjectsFromDeveloperContainer } from '../../serp/components/newobjects_from_developer/container';
import { OffersContainer } from '../../serp/components/offers/container';
import { PromoteMobile } from '../../serp/components/promote_mobile';
import { ComparisonNotificationContainerLoadable } from '../../serp/containers/ComparisonNotificationContainer';
import { EmptyPageSummaryContainer } from '../../serp/containers/EmptyPageSummaryContainer';
import { NewbuildingFiltersContainer } from '../../serp/containers/NewbuildingFilters/NewbuildingFiltersContainer';
import { QuickLinksContainer } from '../../serp/containers/QuickLinksContainer';
import { SeoBotCatcherLinksContainer } from '../../serp/containers/SeoBotCatcherLinksContainer';
import { SeoCrossLinksContainer } from '../../serp/containers/SeoCrossLinksContainer';
import { SeoTabsLinksContainer } from '../../serp/containers/SeoTabsLinksContainer';
import { SubsidisedMortgageZeroPlaceBannerContainer } from '../../serp/containers/SubsidisedMortgageZeroPlaceBannerContainer';
import { SuburbanCatalogsContainer } from '../../serp/containers/SuburbanCatalogsContainer';
import { EHideOfferState, setHideOfferState } from '../../serp/state/hide_offer';
import { parseHash } from '../../utils/hash';
import { saveJsonQueryInLocalStorage } from '../../utils/save_json_query';
import { ChatPopupContainer } from '../ChatPopupContainer';
import { FavoritesFoldersContainer } from '../FavoritesFoldersContainer';
import { FiltersContainer } from '../FiltersContainer';
import { HeaderSectionContainer } from '../HeaderSectionContainer';
import { JournalHighlightsContainer } from '../JournalHighlightsContainer';
import { NewbuildingBlackFridayTopBannerContainer } from '../NewbuildingBlackFridayTopBannerContainer';
import { NewbuildingBrokerCallbackWidgetContainer } from '../NewbuildingBrokerCallbackWidgetContainer';
import { NewbuildingBrokerSelfRequestFormWidgetContainer } from '../NewbuildingBrokerSelfRequestFormWidgetContainer';
import { NewbuildingLayoutsContainer } from '../NewbuildingLayoutsContainer';
import { NewbuildingPromoBuilderOffersContainer } from '../NewbuildingPromoBuilderOffersContainer';
import { LazyNewbuildingPromoCarouselContainer } from '../NewbuildingPromoCarousel';
import { NotAvailablePopupContainer } from '../NotAvailablePopupContainer';
import { PaginationContainer } from '../PaginationContainer';
import { ProfessionalSearchPromoContainer } from '../ProfessionalSearchPromoContainer';
import { SearchStringContainer } from '../SearchStringContainer';
import { SeoMarkedListContainer } from '../SeoMarkedListContainer';
import { SpecialProjectBannerContainer } from '../SpecialProjectBannerContainer';
import { SuggestionsContainer } from '../SuggestionsContainer';
import { VillagesRecommendationsContainer } from '../VillagesRecommendationsContainer';
import { setLoading } from 'shared/actions/search';
import { EtazhiBlackFridayPromotion } from 'shared/components/EtazhiBlackFridayPromotion';
import { SearchStringBlockLayout } from 'shared/components/SearchStringBlockLayout';
import { EarlyAccessBannerContainer } from 'shared/containers/EarlyAccessBannerContainer';
import { EarlyAccessPaywallProvider } from 'shared/containers/EarlyAccessPaywallProvider';
import { usePagination } from 'shared/containers/PaginationContainer/usePagination';
import { SeoTopBannersContainer } from 'shared/containers/SeoTopBannersContainer';
import { selectEarlyAccessData } from 'shared/selectors/earlyAccess';
import { selectIsEtazhiBlackFridayPromoVisible } from 'shared/selectors/etazhiBlackFriday';
import {
  selectHideOfferErrorMessage,
  selectIsHideOfferError,
  selectIsTopPopupOpened,
} from 'shared/selectors/hideOffer';
import { getOffers } from 'shared/selectors/offers';
import { selectIsQuickLinksAvailable } from 'shared/selectors/quickLinks';
import { selectFullUrl, selectJsonQuery, selectQueryString } from 'shared/selectors/results';
import { selectPageNumber } from 'shared/selectors/results/selectPageNumber';
import { selectIsSerpFetching } from 'shared/selectors/selectIsSerpFetching';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { dealTypeFromJsonQuery, FDealType, FOfferType, offerTypeFromJsonQuery } from 'shared/utils/category';

export const SearchEngineResultsPageContainer: React.FC = () => {
  const dispatch = useDispatch();
  const [isMounted, setIsMounted] = React.useState(false);
  const { httpApi, logger, config } = useApplicationContext();

  const deviceType = useSelector((state: IAppState) => state.deviceType);
  const isFetching = useSelector(selectIsSerpFetching);
  const isPrintEnabled = useSelector((state: IAppState) => state.print.enabled);
  const isPromotionMobileClosed = useSelector((state: IAppState) => state.isPromotionMobileClosed);
  const isTopPopupOpened = useSelector(selectIsTopPopupOpened);
  const hideOfferErrorMessage = useSelector(selectHideOfferErrorMessage);
  const isHideOfferError = useSelector(selectIsHideOfferError);
  const isSpecialProjectAdfoxBannerVisible = useSelector(selectIsSpecialProjectAdfoxBannerVisible);
  const isNewbuildingPromoBuilderOffersVisible = useSelector(selectIsNewbuildingPromoBuilderOffersVisible);
  const isResidentialComplexLayouts = useSelector(residentialComplexLayoutsOnSelector);
  const queryString = useSelector(selectQueryString);
  const jsonQuery = useSelector(selectJsonQuery);
  const offersLength = useSelector((state: IAppState) => state.results.offers.length);
  const isQuickLinksAvailable = useSelector(selectIsQuickLinksAvailable);
  const fullUrl = useSelector(selectFullUrl);
  const offers = useSelector(getOffers);
  const page = useSelector(selectPageNumber);
  const comparisonNotification = useSelector((state: IAppState) => state.comparisonNotification);
  const suburbanCatalogsTypes = useSelector(getSuburbanCatalogsTypes);
  const isSeoTabsLinksAvailable = useSelector(getSeoTabsLinksAvailable);
  const isProfessionalSearchPromoBannerVisible = useSelector(selectIsProfessionalSearchPromoBannerVisible);
  const isSubsidisedMortgageZeroPlaceBannerVisible = useSelector(selectIsSubsidisedMortgageZeroPlaceBannerVisible);
  const isEarlyAccessBannerVisible = useSelector(selectEarlyAccessData);
  const isEtazhiBlackFridayPromoVisible = useSelector(selectIsEtazhiBlackFridayPromoVisible);
  const { totalPages } = usePagination();
  const blackFridayBannerVisible = isBlackFridayBannerVisible(jsonQuery);

  //Эксперимент баннер перед бесконечной выдачей CD-44294
  const showPreInfinteSaveSearchBanner = React.useMemo(
    () => Boolean(offersLength) && (totalPages === 1 || page === totalPages),
    [offersLength, totalPages, page],
  );
  const isOfferInSavedSearch = Boolean(jsonQuery.saved_search_id);
  const showTrap = React.useMemo(() => canShowTrap(offers), [offers]);

  const handleSetHideOfferState = React.useCallback(
    (hideOfferState: EHideOfferState) => {
      dispatch(setHideOfferState(hideOfferState));
    },
    [dispatch],
  );

  const handleSetLoading = React.useCallback(() => {
    dispatch(setLoading());
  }, [dispatch]);

  React.useEffect(() => {
    if (
      offerTypeFromJsonQuery(jsonQuery) === FOfferType.FlatOld &&
      dealTypeFromJsonQuery(jsonQuery) === FDealType.Sale
    ) {
      const url = new URL(fullUrl);
      url.pathname = 'cat.php';
      url.search = queryString;

      const globalLog = getGlobalEventsLogs();
      globalLog.produce('residentialComplexesTooltip', { type: 'open', value: url.toString() });
    }

    saveJsonQueryInLocalStorage(jsonQuery);

    /**
     * При уходе с выдачи по сохраненному поиску отметить все "новые" оферы просмотренными
     */
    const handleBeforeUnload = () => {
      const searchParams = new URLSearchParams(queryString);
      if (searchParams.has('saved_search_id')) {
        const savedSearchId = Number(searchParams.get('saved_search_id'));
        readAllOffersForSavedSearches(httpApi, { savedSearchId }, logger);
      }
    };

    const handlePopState = async () => {
      handleSetLoading();
      window.location.reload();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    const parsedHash = parseHash(window.location.hash);
    if (Object.keys(parsedHash).includes('HIDE')) {
      handleSetHideOfferState(parsedHash['HIDE'] as EHideOfferState);
      history.pushState('', document.title, window.location.pathname + window.location.search);
    }

    setIsMounted(true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      setIsMounted(false);
    };
  }, [jsonQuery, fullUrl, logger, handleSetHideOfferState, handleSetLoading, httpApi, queryString]);

  const lkUrl = config.get<string>('hiddenObjects.lkUrl') || '';
  const isSearchStringEnabled = config.get<boolean>('BX.SearchString.Enabled') || false;

  return (
    <div
      className={classNames(style['serp'], style['serp--light'])}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <ErrorLogComponent logger={logger}>
        {isSearchStringEnabled ? (
          <SearchStringBlockLayout
            searchStringBlock={<SearchStringContainer />}
            filtersBlock={<FiltersContainer isRedesignEnabled />}
          />
        ) : (
          <FiltersContainer />
        )}
      </ErrorLogComponent>
      {isFetching && (
        <div className={style['preloadOverlay']} data-testid="PagePreloader">
          <div className={style['preloader']}>
            <Spinner size={50} />
          </div>
        </div>
      )}
      <ErrorLogComponent logger={logger}>
        <HeaderSectionContainer />
      </ErrorLogComponent>

      <ErrorLogComponent logger={logger}>
        <EmptyPageSummaryContainer />
      </ErrorLogComponent>

      {deviceType !== 'desktop' && !isPromotionMobileClosed && (
        <ErrorLogComponent logger={logger}>
          <PromoteMobile />
        </ErrorLogComponent>
      )}
      {isSeoTabsLinksAvailable && (
        <ErrorLogComponent logger={logger}>
          <SeoTabsLinksContainer />
        </ErrorLogComponent>
      )}
      {isQuickLinksAvailable && !isSeoTabsLinksAvailable && (
        <ErrorLogComponent logger={logger}>
          <QuickLinksContainer
            banner={
              suburbanCatalogsTypes.length <= 1 ? (
                <SuburbanCatalogsContainer catalogsTypes={suburbanCatalogsTypes} />
              ) : undefined
            }
          />
        </ErrorLogComponent>
      )}
      <ErrorLogComponent logger={logger}>
        <NewbuildingFiltersContainer />
      </ErrorLogComponent>
      {(!isQuickLinksAvailable || suburbanCatalogsTypes.length > 1) && (
        <ErrorLogComponent logger={logger}>
          <SuburbanCatalogsContainer catalogsTypes={suburbanCatalogsTypes} fullWidth />
        </ErrorLogComponent>
      )}
      <ErrorLogComponent logger={logger}>
        {isEtazhiBlackFridayPromoVisible ? (
          <EtazhiBlackFridayPromotion />
        ) : isEarlyAccessBannerVisible ? (
          <EarlyAccessBannerContainer />
        ) : isSubsidisedMortgageZeroPlaceBannerVisible ? (
          <SubsidisedMortgageZeroPlaceBannerContainer />
        ) : isResidentialComplexLayouts ? null : isNewbuildingPromoBuilderOffersVisible ? (
          <NewbuildingPromoBuilderOffersContainer />
        ) : isSpecialProjectAdfoxBannerVisible ? (
          <SpecialProjectBannerContainer />
        ) : isProfessionalSearchPromoBannerVisible ? (
          <ProfessionalSearchPromoContainer />
        ) : blackFridayBannerVisible ? (
          <NewbuildingBlackFridayTopBannerContainer />
        ) : null}
      </ErrorLogComponent>
      <ErrorLogComponent logger={logger}>
        {isResidentialComplexLayouts ? (
          <NewbuildingLayoutsContainer />
        ) : (
          <EarlyAccessPaywallProvider>
            <OffersContainer />
            <EarlyAccessPaywallContainer />
          </EarlyAccessPaywallProvider>
        )}
      </ErrorLogComponent>
      <ErrorLogComponent logger={logger}>
        {isResidentialComplexLayouts ? null : <NewobjectsContainer />}
      </ErrorLogComponent>
      <ErrorLogComponent logger={logger}>
        {isResidentialComplexLayouts ? null : <NewobjectsFromDeveloperContainer />}
      </ErrorLogComponent>

      <ErrorLogComponent logger={logger}>
        {!isResidentialComplexLayouts && showPreInfinteSaveSearchBanner && (
          <PreInfiniteBanner type={EPreInfiniteBannerTypes.subscription} isOfferInSavedSearch={isOfferInSavedSearch} />
        )}
      </ErrorLogComponent>

      <ErrorLogComponent logger={logger}>{!isResidentialComplexLayouts && <SuggestionsContainer />}</ErrorLogComponent>

      <ErrorLogComponent logger={logger}>{!isResidentialComplexLayouts && <PaginationContainer />}</ErrorLogComponent>

      <ErrorLogComponent logger={logger}>
        {!isResidentialComplexLayouts && <LazyNewbuildingPromoCarouselContainer />}
      </ErrorLogComponent>

      <ErrorLogComponent logger={logger}>
        {!isResidentialComplexLayouts && <VillagesRecommendationsContainer />}
      </ErrorLogComponent>

      {!isResidentialComplexLayouts && showTrap && (
        <div className={style['wrapper']} data-testid="realtor_request_trap">
          <ErrorLogComponent logger={logger}>
            <RealtorRequestTrapContainer />
          </ErrorLogComponent>
        </div>
      )}
      <ErrorLogComponent logger={logger}>
        <FastLinksContainer />
      </ErrorLogComponent>
      {!isPrintEnabled && (
        <ErrorLogComponent logger={logger}>
          <SeoTopBannersLayout>
            <SeoTopBannersContainer />
          </SeoTopBannersLayout>
        </ErrorLogComponent>
      )}

      <ErrorLogComponent logger={logger}>
        <JournalHighlightsContainer />
      </ErrorLogComponent>

      <ErrorLogComponent logger={logger}>
        <SeoCrossLinksContainer />
      </ErrorLogComponent>

      {isMounted && isTopPopupOpened && (
        <HideOfferTopPopup isError={isHideOfferError} lkUrl={lkUrl} errorMessage={hideOfferErrorMessage} />
      )}
      <ChatsContainerLoadable />
      <ChatPopupContainer />

      <NotAvailablePopupContainer />

      <FavoritesFoldersContainer />

      <NewbuildingBrokerSelfRequestFormWidgetContainer />
      <NewbuildingBrokerCallbackWidgetContainer />

      <ErrorLogComponent logger={logger}>
        <SeoBotCatcherLinksContainer />
      </ErrorLogComponent>
      {comparisonNotification && <ComparisonNotificationContainerLoadable notification={comparisonNotification} />}

      <SeoMarkedListContainer />
    </div>
  );
};
